import { useMemo } from 'react'
import styles from './info.module.css'

export type Liquid3DMaterialSelection = {
  colour: string;
  elevationThickness: string;
  edgeType: string;
  edgeWidth: string;
  texture: string;
};

const decodeDescription = (description: string): string => {
  return description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>|<\/div>|<\/li>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim();
};

export const getLiquid3DMaterialSelections = (description: string): Liquid3DMaterialSelection[] => {
  const decodedDescription = decodeDescription(description);
  const tableLines = decodedDescription.split('\n').map(line => line.trim());
  const tableHeaderIndex = tableLines.findIndex(line => /^colou?r\s*\|\s*elevation/i.test(line));

  if (tableHeaderIndex >= 0) {
    const headers = tableLines[tableHeaderIndex].split('|').map(header => header.trim().toLowerCase());
    const getColumnValue = (values: string[], pattern: RegExp): string =>
      values[headers.findIndex(header => pattern.test(header))] ?? '';

    return tableLines
      .slice(tableHeaderIndex + 1)
      .filter(line => line.includes('|') && !/^[-\s|]+$/.test(line))
      .map(line => line.split('|').map(value => value.trim()))
      .map(values => ({
        colour: getColumnValue(values, /^colou?r$/),
        elevationThickness: getColumnValue(values, /elevation/),
        edgeType: getColumnValue(values, /edge.*type/),
        edgeWidth: getColumnValue(values, /edge.*width/),
        texture: getColumnValue(values, /texture/),
      }));
  }

  const selectionDetails = decodedDescription.match(/SELECTION DETAILS\s*([\s\S]*)/i)?.[1] ?? '';

  return selectionDetails
    .split(/(?=Colou?r:\s*)/i)
    .map(selection => ({
      colour: selection.match(/Colou?r:\s*([^\n-]+)/i)?.[1]?.trim() ?? '',
      elevationThickness: selection.match(/Elevation Thickness:\s*([^\n]+)/i)?.[1]?.trim() ?? '',
      edgeType: selection.match(/Edge Type:\s*([^\n]+)/i)?.[1]?.trim() ?? '',
      edgeWidth: selection.match(/Edge Width:\s*([^\n]+)/i)?.[1]?.trim() ?? '',
      texture: selection.match(/Texture:\s*([^\n]+)/i)?.[1]?.trim() ?? '',
    }))
    .filter(selection => selection.colour !== '');
};

const colorSwatches: Record<string, string> = {
  'parrot green': '#a8d600',
  sashimi: '#ff6049',
};

const getColorSwatch = (colour: string): string => colorSwatches[colour.toLowerCase()] ?? colour;

interface Liquid3DMaterialSelectionDetailsProps {
  description: string;
  columnClassName: string;
  bodyRef?: (element: HTMLDivElement | null) => void;
}

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.details}>
    <span className={styles.name}>{label}:</span>
    <span className={styles.value}>{value}</span>
  </div>
);

export default function Liquid3DMaterialSelectionDetails({
  description,
  columnClassName,
  bodyRef,
}: Liquid3DMaterialSelectionDetailsProps) {
  const selections = useMemo(() => getLiquid3DMaterialSelections(description), [description]);

  if (selections.length === 0) return null;

  return (
    <div className={[styles.card, columnClassName].join(' ')}>
      <h3 className={styles.title}>Material Selection Details</h3>
      <div className={styles.cardBody} ref={bodyRef}>
        {selections.map((selection, index) => (
          <div className={styles.liquid3DSelection} key={`${selection.colour}-${index}`}>
            <div className={styles.details}>
              <span className={styles.name}>Colour:</span>
              <span
                aria-hidden="true"
                className={styles.liquid3DColourSwatch}
                style={{ backgroundColor: getColorSwatch(selection.colour) }}
              />
              <span className={styles.value}>{selection.colour}</span>
            </div>
            <Detail label="- Elevation Thickness" value={selection.elevationThickness} />
            <Detail label="- Edge Type" value={selection.edgeType} />
            {selection.edgeWidth && <Detail label="- Edge Width" value={selection.edgeWidth} />}
            <Detail label="- Texture" value={selection.texture} />
          </div>
        ))}
      </div>
    </div>
  );
}
